import { compose } from 'redux';

import { Action } from '../../actions';
import {
    ADD_SMART_FILTER,
    CHANGE_SMART_FILTER_TAB,
    ChangeSmartFilterTabAction,
    CLOSE_GROUP_FILTER_INSPECTOR,
    CloseGroupFilterInspectorAction,
    OPEN_GROUP_FILTER_INSPECTOR,
    OpenGroupFilterInspectorAction,
    SELECT_AUTOBUDGET_PRIORITY_TYPE,
    SET_FILTER_FEED_CATEGORIES,
    SET_SMART_FILTER_VALIDATION_ERRORS,
    SetFilterFeedCategoriesAction,
    SetSmartFilterValidationErrorsAction,
    TOGGLE_SMART_FILTER_AVAILABLE,
    ToggleSmartFilterAvailableAction,
    UPDATE_AUTOBUDGET_PRICE,
    UPDATE_FEED_FILTER_SCHEMA,
    UPDATE_FILTERS_ERROR,
    UPDATE_SMART_FILTER,
    UPDATE_SMART_FILTER_AUDITORY,
    UPDATE_SMART_FILTER_CATEGORIES,
    UPDATE_SMART_FILTER_NAME,
    UPDATE_SMART_FILTER_PRICE_RANGE,
    UPDATE_SMART_FILTER_SUCCESS,
    UPDATE_SMART_FILTER_VENDORS,
    UpdateAutobudgetPriceAction,
    UpdateFiltersErrorAction,
    UpdateSmartFilterAuditoryAction,
    UpdateSmartFilterCategoriesAction,
    UpdateSmartFilterNameAction,
    UpdateSmartFilterPriceRangeAction,
    UpdateSmartFilterSuccessAction,
    UpdateSmartFilterVendorsAction
} from '../../actions/groupFilterInspector';
import { makeEmptySmartFilterItem } from '../../mappers/group/group';
import { FeedCategoriesHelpers } from '../../service/FeedCategoriesHelpers/FeedCategoriesHelpers';
import { PageStatus, ValidationErrorsData } from '../../typings/common';
import {
    EditGroupFilterItem,
    EditGroupsState
} from '../../typings/groupsEditState';

const resetErrors = (
    filter: EditGroupFilterItem,
    field: string
): ValidationErrorsData => {
    return {
        ...filter.errors,
        [field]: {}
    };
};

const openGroupFilterInspectorReducer = (
    state: EditGroupsState,
    action: OpenGroupFilterInspectorAction
): EditGroupsState => {
    const { groupId, filter, feedId, campaign } = action.payload;

    const resultFilter = filter || makeEmptySmartFilterItem(campaign);

    return {
        ...state,
        groupFilterInspector: {
            ...state.groupFilterInspector,
            adGroupId: groupId,
            filterId: resultFilter.id,
            feedId,
            filter: resultFilter,
            isOpen: true,
            inspectorStatus: PageStatus.LOADING
        }
    };
};

const closeGroupFilterInspectorReducer = (
    state: EditGroupsState,
    action: CloseGroupFilterInspectorAction
): EditGroupsState => {
    return {
        ...state,
        groupFilterInspector: {
            ...state.groupFilterInspector,
            isOpen: false
        }
    };
};

const setFilterFeedCategories = (
    state: EditGroupsState,
    action: SetFilterFeedCategoriesAction
): EditGroupsState => {
    const { categories } = action;

    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    const feedCategories = compose(
        FeedCategoriesHelpers.selectParents,
        nodes => FeedCategoriesHelpers.generateTree(nodes, filter.categoryIds),
        FeedCategoriesHelpers.sortDeleted,
        FeedCategoriesHelpers.changeDeletedCategoriesParent
    )(categories);

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...filter,
                feedCategories
            }
        }
    };
};

const updateSmartFilterCategories = (
    state: EditGroupsState,
    action: UpdateSmartFilterCategoriesAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...filter,
                feedCategories: action.categories,
                errors: {
                    ...filter.errors,
                    categoryIds: {},
                    filterItself: {}
                }
            }
        }
    };
};

const updateSmartFilterVendors = (
    state: EditGroupsState,
    action: UpdateSmartFilterVendorsAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...filter,
                vendors: action.vendors,
                errors: resetErrors(filter, 'vendors')
            }
        }
    };
};

const updateSmartFilterPriceRange = (
    state: EditGroupsState,
    action: UpdateSmartFilterPriceRangeAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...filter,
                priceRange: action.priceRange,
                errors: resetErrors(filter, 'priceRange')
            }
        }
    };
};

const toggleSmartFilterAvailable = (
    state: EditGroupsState,
    action: ToggleSmartFilterAvailableAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...filter,
                isAvailable: !filter.isAvailable
            }
        }
    };
};

const changeSmartFilterTab = (
    state: EditGroupsState,
    action: ChangeSmartFilterTabAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...filter,
                tab: action.tab,
                errors: {
                    ...filter.errors,
                    filterItself: {}
                }
            }
        }
    };
};

const updateSmartFilterAuditory = (
    state: EditGroupsState,
    action: UpdateSmartFilterAuditoryAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...groupFilterInspector.filter,
                type: action.value,
                errors: {
                    ...filter.errors,
                    filterItself: {}
                }
            }
        }
    };
};

const setSmartFilterValidationErrors = (
    state: EditGroupsState,
    action: SetSmartFilterValidationErrorsAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;
    const { errors } = action;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            inspectorStatus: PageStatus.LOADED,
            filter: {
                ...filter,
                errors,
                conditions: filter.conditions.map((condition, index) => {
                    const conditionErrors =
                        errors.conditions && errors.conditions[index];
                    return {
                        ...condition,
                        errors: conditionErrors ? conditionErrors.errors : []
                    };
                })
            }
        }
    };
};

const updateFiltersError = (
    state: EditGroupsState,
    action: UpdateFiltersErrorAction
): EditGroupsState => {
    return {
        ...state,
        groupFilterInspector: {
            ...state.groupFilterInspector,
            inspectorStatus: PageStatus.LOADED
        }
    };
};

const updateAutobudgetPrice = (
    state: EditGroupsState,
    action: UpdateAutobudgetPriceAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...filter,
                [action.cpaOrCpc]: action.value,
                errors: resetErrors(filter, action.cpaOrCpc)
            }
        }
    };
};

const updateSmartFilterName = (
    state: EditGroupsState,
    action: UpdateSmartFilterNameAction
): EditGroupsState => {
    const {
        groupFilterInspector,
        groupFilterInspector: { filter }
    } = state;

    return {
        ...state,
        groupFilterInspector: {
            ...groupFilterInspector,
            filter: {
                ...filter,
                name: action.value,
                errors: resetErrors(filter, 'name')
            }
        }
    };
};

const updateSmartFilterSuccess = (
    state: EditGroupsState,
    action: UpdateSmartFilterSuccessAction
): EditGroupsState => {
    return {
        ...state,
        groupFilterInspector: {
            ...state.groupFilterInspector,
            isOpen: false,
            inspectorStatus: PageStatus.LOADED
        }
    };
};

export const groupFilterInspectorReducer = (
    state: EditGroupsState,
    action: Action
): EditGroupsState => {
    switch (action.type) {
        case OPEN_GROUP_FILTER_INSPECTOR:
            return openGroupFilterInspectorReducer(state, action);

        case CLOSE_GROUP_FILTER_INSPECTOR:
            return closeGroupFilterInspectorReducer(state, action);

        case UPDATE_FEED_FILTER_SCHEMA:
            return {
                ...state,
                groupFilterInspector: {
                    ...state.groupFilterInspector,
                    schema: action.schema,
                    inspectorStatus: PageStatus.LOADED
                }
            };
        case SET_FILTER_FEED_CATEGORIES:
            return setFilterFeedCategories(state, action);

        case SELECT_AUTOBUDGET_PRIORITY_TYPE:
            return {
                ...state,
                groupFilterInspector: {
                    ...state.groupFilterInspector,
                    filter: {
                        ...state.groupFilterInspector.filter,
                        autobudgetPriority: action.priority
                    }
                }
            };

        case UPDATE_AUTOBUDGET_PRICE:
            return updateAutobudgetPrice(state, action);
        case UPDATE_SMART_FILTER_NAME:
            return updateSmartFilterName(state, action);
        case ADD_SMART_FILTER:
        case UPDATE_SMART_FILTER:
            return {
                ...state,
                groupFilterInspector: {
                    ...state.groupFilterInspector,
                    inspectorStatus: PageStatus.LOADING
                }
            };

        case UPDATE_SMART_FILTER_AUDITORY:
            return updateSmartFilterAuditory(state, action);

        case UPDATE_SMART_FILTER_CATEGORIES:
            return updateSmartFilterCategories(state, action);
        case UPDATE_SMART_FILTER_VENDORS:
            return updateSmartFilterVendors(state, action);
        case UPDATE_SMART_FILTER_PRICE_RANGE:
            return updateSmartFilterPriceRange(state, action);
        case TOGGLE_SMART_FILTER_AVAILABLE:
            return toggleSmartFilterAvailable(state, action);
        case CHANGE_SMART_FILTER_TAB:
            return changeSmartFilterTab(state, action);
        case SET_SMART_FILTER_VALIDATION_ERRORS:
            return setSmartFilterValidationErrors(state, action);
        case UPDATE_FILTERS_ERROR:
            return updateFiltersError(state, action);
        case UPDATE_SMART_FILTER_SUCCESS:
            return updateSmartFilterSuccess(state, action);
        default:
            return state;
    }
};
